const DEFAULT_API_BASE = 'https://aexgora.onrender.com';

const apiBase = (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/$/, '') || DEFAULT_API_BASE;

export interface OperatorProfile {
  operator_id: string;
  email: string;
  aex_id: string;
  public_key: string;
  display_name: string;
  stripe_account_id: string | null;
  stripe_customer_id: string | null;
  payout_enabled: boolean;
  /** null = unverified. Absent on a server older than job 72e40a5c. */
  email_verified_at?: string | null;
  balance: number;
  created_at: string;
  updated_at: string;
}

export interface RegisterResponse {
  operator_id: string;
  aex_id: string;
  session_token?: string;
}

export interface LoginResponse {
  session_token: string;
  operator: {
    operator_id: string;
    email: string;
    aex_id: string;
    display_name: string;
    [key: string]: unknown;
  };
}

export interface SpendHaltState {
  halted: boolean;
  reason: string | null;
  since: string | null;
}

export interface PlatformHealth {
  status: string;
  db: string;
  commit: string;
  hung_jobs: { blocked: number; dead: number };
  spend: SpendHaltState & {
    windows: { aex_24h: number; stripe_usd_24h: number; anthropic_usd_24h: number };
    last_swept_at?: string;
  };
  webhook_signing?: { mode: string; unsigned_emitted: number; refused: number };
  timestamp: string;
}

export interface HungJob {
  id: string;
  title: string;
  stoa_status: string;
  offered_rate: number;
  assigned_agent_id: string | null;
  classification?: string;
  age_minutes?: number;
  [key: string]: unknown;
}

/** Per-surface agent key (migration 085). Never carries secret material. */
export interface AgentSubkey {
  subkey_id: string;
  agent_id: string;
  label: string;
  stoa_id: string | null;
  public_key_hex: string;
  created_at: string;
  revoked_at: string | null;
  revoked_reason: string | null;
  last_used_at: string | null;
  active: boolean;
}

/**
 * Board-side seat switch. The console never talks to the Box; the Box is meant
 * to sync these DOWN to its switch files via scripts/duty/sync-switches.mjs.
 *
 * !! As of 2026-08-17 that sync HAS NO CALLER, so toggling a switch here does
 * NOT stop the Box. This UI reads back the same table it just wrote, so it will
 * confirm a stop that did not happen. Do not present switch state as Box state
 * until the control path is wired and acknowledged. See migration 087. !!
 */
export interface DutySwitch {
  switch_key: string;
  kind: 'seat' | 'global' | 'product';
  switched_on: boolean;
  reason: string | null;
  updated_by: string | null;
  updated_at: string;
  created_at: string;
  /** False = a bound seat with no switch row yet (never flipped; reads OFF). */
  materialized?: boolean;
}

export interface DutySwitchLogEntry {
  id: string;
  switch_key: string;
  switched_on: boolean;
  reason: string | null;
  changed_by: string | null;
  changed_at: string;
}

export interface StoaJob {
  id: string;
  title: string;
  description: string;
  stoa_status: string;
  offered_rate: number;
  rate_currency: string;
  rate_unit: string;
  operator_id: string;
  stall_id: string;
  assigned_agent_id: string | null;
  escrow_payment_id: string | null;
  receipt_id: string | null;
  delivery_note: string | null;
  delivery_link: string | null;
  delivery_child_stall_id: string | null;
  delivery_result_ergon_id: string | null;
  rejection_reason: string | null;
  is_private: boolean;
  expires_at: string | null;
  hired_at: string | null;
  in_progress_at: string | null;
  delivered_at: string | null;
  accepted_at: string | null;
  rejected_at: string | null;
  cancelled_at: string | null;
  settled_at: string | null;
  // ACL v0.1 items 3 & 4 (Ponder 2026-05-17). Populated for agent-posted
  // jobs via POST /stoa/jobs/by-agent; null on operator-posted jobs.
  posted_by_agent_id: string | null;
  parent_job_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface StallWallet {
  stall_id: string;
  stall_name: string;
  balance: number;
}

export interface ConsoleBalance {
  operator_balance: number;
  stall_wallets: StallWallet[];
}

export interface ConsoleStall {
  stall_id: string;
  name: string;
  description: string | null;
  is_live: boolean;
  is_certified: boolean;
  // Visibility (founder ruling 2026-08-13): private = colony seat, public =
  // a product anyone can approach. Surfaced per-row so a live-but-private
  // stall is readable at a glance (PR #115 spec §4).
  is_private: boolean;
  wallet_balance: number;
  // agent_id linked to this stall (per Synedrion umbrella: each persona's
  // stall row carries its agent_id). NULL when the stall hasn't been
  // wired to a specific worker yet (e.g. Myrmekia per migration 061).
  agent_id: string | null;
  // Soft-delete marker (migration 070). NULL for active stalls; ISO
  // timestamp for archived stalls — only present in API responses when
  // the caller passed include_archived=1.
  archived_at: string | null;
  dex_stats: { job_count: number; hire_count: number };
  // Job-intake posture (K-6, AEXgora #240): webhook vs post_only honesty
  // badge material. Optional because older responses may omit it.
  job_intake?: {
    webhook_url: string | null;
    posted_jobs: number;
    posted_post_only: number;
    overtime: boolean | null;
  };
}

/**
 * Row shape of GET /stoa/console/agents — every agent whose operator_id is
 * the caller, in every lifecycle state.
 *
 * `retired_at` / `is_listed` are OPTIONAL because the endpoint does not
 * return them yet (stoa-console.ts selects four columns; the lifecycle
 * columns exist on the table but are not projected). The console must not
 * pretend otherwise: treat `undefined` as "state not reported by the
 * server", which is different from `null` ("reported: not retired"). The
 * moment the server adds the columns, the Agents tab and the view-as
 * filter start using them with no client change — that contract ask is
 * filed on the board (R-V1/R-V2, job 42dc2eef).
 */
export interface ConsoleAgent {
  agent_id: string;
  aex_id: string;
  display_name: string;
  agent_type: string;
  retired_at?: string | null;
  is_listed?: boolean;
}

// ---- Tenant onboarding (PR #115 spec) --------------------------------------

export type AgentType = 'autonomous' | 'human_operated' | 'hybrid' | 'ergaster' | 'standard';

/**
 * POST /agents/register, action=generate_identity (Path A). The response is
 * the ONLY copy of private_key that will ever exist — the caller must show
 * it once, require an explicit acknowledgement, and never persist it
 * (PR #115 spec §3).
 */
export interface RegisterAgentPathAResponse {
  agent_id: string;
  aex_id: string;
  public_key: string;
  private_key: string;
  display_name: string;
  agent_type: string;
  [key: string]: unknown;
}

/** Path B response — the server never held the key, so none comes back. */
export interface RegisterAgentPathBResponse {
  agent_id: string;
  aex_id: string;
  public_key: string;
  display_name: string;
  agent_type: string;
  [key: string]: unknown;
}

export interface AuthChallenge {
  challenge: string;
  expires_at: string;
}

/**
 * POST /stoa/stalls answers 201 with { stall_id, stall_aex_id,
 * wallet_balance } only. The other fields are optional so the flow cannot
 * mistake an absent echo for a value (the Koroi Labs walk, job 171a06d5:
 * "visibility as chosen" failed on a public stall that was public).
 */
export interface CreateStallResponse {
  stall_id: string;
  stall_aex_id?: string;
  wallet_balance: number;
  name?: string;
  agent_id?: string | null;
  is_private?: boolean;
  is_live?: boolean;
  is_certified?: boolean;
  [key: string]: unknown;
}

/**
 * Unauthenticated probe of the public stall page projection — the PR #115
 * spec §4 acceptance check ("create a public stall, then GET /page with no
 * auth and get a 200"). Deliberately NOT routed through the operator client:
 * a Bearer header would make the probe lie about what an anonymous human
 * sees. Returns the raw status; the caller decides what it means for the
 * visibility that was chosen.
 */
export async function probeStallPageAnonymous(stallId: string): Promise<{ status: number }> {
  try {
    const res = await fetch(`${apiBase}/api/v1/stoa/stalls/${stallId}/page`, { method: 'GET' });
    return { status: res.status };
  } catch {
    return { status: 0 };
  }
}

/** A content item as POST/GET /stoa/stalls/:id/content return it. */
export interface StallContentItem {
  stall_id: string;
  slug: string;
  type: string;
  title: string;
  summary: string | null;
  price_aex: number | null;
  visibility: string;
  [key: string]: unknown;
}

/** An offering as the stall manifest publishes it. */
export interface StallOffering {
  slug: string;
  kind: string;
  label: string;
  description: string | null;
  price_aex: number | null;
  hire_url: string | null;
  offering_hash: string;
}

// Chat-protocol layer (Founder 2026-05-20). Comments are the fast-path
// inline conversation on a job.
export interface StoaJobComment {
  comment_id: string;
  job_id: string;
  author_agent_id: string;
  body: string;
  signature: string;
  created_at: string;
}

export interface StoaReceipt {
  receipt_id: string;
  job_id: string;
  caller_aex_id: string;
  provider_aex_id: string;
  amount_aex: number;
  exchange_rate: number;
  amount_usd: number;
  sequence_number: number;
  signature: string;
  signed_payload: string;
  stripe_transfer_id: string | null;
  created_at: string;
}

export class ApiError extends Error {
  status: number;
  code: string;
  details?: unknown;
  /**
   * The full parsed error body. Some routes put structured context at the
   * top level rather than under `details` — e.g. the retire 409 carries
   * `live_stalls` ("archive the stall first") beside `message`. Kept so the
   * UI can render the dependency instead of a bare refusal.
   */
  body?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.body = body;
  }
}

export interface OperatorApiOptions {
  getToken: () => string | null;
  onUnauthorized: () => void;
}

async function request<T>(
  method: string,
  path: string,
  body: unknown,
  token: string | null,
  onUnauthorized: () => void,
): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(`${apiBase}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (err) {
    throw new ApiError(0, 'NETWORK_ERROR', err instanceof Error ? err.message : 'Network error');
  }

  let payload: any = null;
  const text = await res.text();
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = { message: text };
    }
  }

  if (!res.ok) {
    if (res.status === 401) onUnauthorized();
    throw new ApiError(
      res.status,
      payload?.error ?? 'UNKNOWN_ERROR',
      payload?.message ?? res.statusText,
      payload?.details,
      payload,
    );
  }

  return payload as T;
}

export function createOperatorApi(opts: OperatorApiOptions) {
  const call = <T>(method: string, path: string, body?: unknown) =>
    request<T>(method, path, body, opts.getToken(), opts.onUnauthorized);

  return {
    apiBase,
    registerOperator: (email: string, password: string, displayName: string) =>
      call<RegisterResponse>('POST', '/api/v1/operators/register', {
        email,
        password,
        display_name: displayName,
      }),
    loginOperator: (email: string, password: string) =>
      call<LoginResponse>('POST', '/api/v1/operators/login', { email, password }),
    logoutOperator: () => call<{ message: string }>('POST', '/api/v1/operators/logout', undefined),
    getOperatorProfile: () => call<OperatorProfile>('GET', '/api/v1/operators/profile', undefined),
    // Recovery + verification (server job 72e40a5c, src/routes/operator-recovery.ts).
    // The three token/email calls are public; the server answers forgot with
    // one neutral body whether or not the address has an account.
    forgotPassword: (email: string) =>
      call<{ message: string }>('POST', '/api/v1/operators/password/forgot', { email }),
    resetPassword: (token: string, newPassword: string) =>
      call<{ message: string }>('POST', '/api/v1/operators/password/reset', {
        token,
        new_password: newPassword,
      }),
    verifyEmail: (token: string) =>
      call<{ email_verified_at: string }>('POST', '/api/v1/operators/email/verify', { token }),
    requestEmailVerification: () =>
      call<{ status: 'sent' | 'already_verified' | 'mail_off' }>(
        'POST', '/api/v1/operators/email/verify/request', undefined
      ),
    getConsoleJobs: (params?: { stoa_status?: string; limit?: number; offset?: number }) => {
      const qs = new URLSearchParams();
      if (params?.stoa_status) qs.set('stoa_status', params.stoa_status);
      if (params?.limit) qs.set('limit', String(params.limit));
      if (params?.offset) qs.set('offset', String(params.offset));
      const q = qs.toString();
      return call<{ jobs: StoaJob[]; total: number; limit: number; offset: number }>(
        'GET', `/api/v1/stoa/console/jobs${q ? `?${q}` : ''}`, undefined
      );
    },
    getConsoleBalance: () =>
      call<ConsoleBalance>('GET', '/api/v1/stoa/console/balance', undefined),
    getConsoleStalls: (params?: { includeArchived?: boolean }) => {
      const qs = new URLSearchParams();
      if (params?.includeArchived) qs.set('include_archived', '1');
      const q = qs.toString();
      return call<{ stalls: ConsoleStall[] }>(
        'GET', `/api/v1/stoa/console/stalls${q ? `?${q}` : ''}`, undefined
      );
    },
    // Soft-delete (archive) a stall. Server-side sets archived_at +
    // is_live=false; row persists for job FK / audit. See migration 070
    // and DELETE /stoa/stalls/:id (stoa-stalls.ts).
    archiveStall: (stallId: string) =>
      call<{ stall_id: string; archived_at: string | null }>(
        'DELETE', `/api/v1/stoa/stalls/${stallId}`, undefined
      ),
    // Un-archive companion. Clears archived_at; does NOT auto-flip is_live
    // back — operator must explicitly go-live again.
    restoreStall: (stallId: string) =>
      call<{ stall_id: string; archived_at: string | null }>(
        'POST', `/api/v1/stoa/stalls/${stallId}/restore`, undefined
      ),
    // Operator grants operating budget to a stall (budget-grant mneme
    // 2026-05-26, operational). Transfers AEX from operator balance to
    // the stall's wallet. Returns the new wallet balance.
    fundStall: (stallId: string, amount: number) =>
      call<{ stall_id: string; wallet_balance: number; granted: number }>(
        'POST', `/api/v1/stoa/stalls/${stallId}/fund`, { amount }
      ),
    getConsoleReceipts: (params?: { limit?: number; offset?: number }) => {
      const qs = new URLSearchParams();
      if (params?.limit) qs.set('limit', String(params.limit));
      if (params?.offset) qs.set('offset', String(params.offset));
      const q = qs.toString();
      return call<{ receipts: StoaReceipt[]; total: number }>(
        'GET', `/api/v1/stoa/console/receipts${q ? `?${q}` : ''}`, undefined
      );
    },
    getJob: (jobId: string) =>
      call<StoaJob>('GET', `/api/v1/stoa/jobs/${jobId}`, undefined),
    getJobReceipt: (jobId: string) =>
      call<StoaReceipt>('GET', `/api/v1/stoa/jobs/${jobId}/receipt`, undefined),
    cancelJob: (jobId: string) =>
      call<{ id: string; stoa_status: 'cancelled'; refunded_aex: number }>(
        'PUT', `/api/v1/stoa/jobs/${jobId}/cancel`, undefined
      ),
    createJob: (input: {
      stall_id: string;
      title: string;
      description: string;
      offered_rate: number;
      is_private?: boolean;
      assigned_agent_id?: string;
      rate_currency?: string;
      rate_unit?: string;
      expires_at?: string | null;
    }) =>
      call<{ id: string; escrow_payment_id: string; stoa_status: 'posted'; expires_at: string | null }>(
        'POST', '/api/v1/stoa/jobs', input
      ),
    hireJob: (jobId: string, agentId: string) =>
      call<{ id: string; stoa_status: 'hired' }>(
        'PUT', `/api/v1/stoa/jobs/${jobId}/hire`, { agent_id: agentId }
      ),
    startJob: (jobId: string) =>
      call<{ id: string; stoa_status: 'in_progress' }>(
        'PUT', `/api/v1/stoa/jobs/${jobId}/start`, undefined
      ),
    deliverAsAgent: (jobId: string, input: {
      delivery_note?: string;
      delivery_link?: string;
      child_stall_id?: string;
      result_ergon_id?: string;
    }) =>
      call<{ id: string; stoa_status: 'delivered' }>(
        'PUT', `/api/v1/stoa/jobs/${jobId}/deliver-as-agent`, input
      ),
    acceptJob: (jobId: string) =>
      call<{ id: string; stoa_status: 'accepted' }>(
        'PUT', `/api/v1/stoa/jobs/${jobId}/accept`, undefined
      ),
    rejectJob: (jobId: string, reason: string) =>
      call<{ id: string; stoa_status: 'rejected' }>(
        'PUT', `/api/v1/stoa/jobs/${jobId}/reject`, { rejection_reason: reason }
      ),
    settleJob: (jobId: string) =>
      call<{ receipt_id: string; signature: string; signed_payload: string; amount_aex: number; platform_fee_aex: number; stall_payout_aex: number; exchange_rate: number; amount_usd: number; stripe_transfer_id: string | null }>(
        'PUT', `/api/v1/stoa/jobs/${jobId}/settle`, undefined
      ),
    getConsoleAgents: () =>
      call<{ agents: ConsoleAgent[] }>(
        'GET', '/api/v1/stoa/console/agents', undefined
      ),

    // ---- Agent lifecycle (R-V1, job 42dc2eef) ----------------------------
    // Retirement hides an agent from discovery and closes its auth; it is
    // reversible and never destroys identity (migration 086). The server
    // 409s a retire while the agent still works live stalls — the ApiError's
    // `details` is unset on that path, but the body carries `live_stalls`,
    // surfaced via the error message and the typed response below.
    retireAgent: (agentId: string, reason?: string) =>
      call<{ agent_id: string; retired_at: string }>(
        'POST', `/api/v1/agents/${agentId}/retire`, reason ? { reason } : {}
      ),
    restoreAgent: (agentId: string) =>
      call<{ agent_id: string; retired_at: null }>(
        'POST', `/api/v1/agents/${agentId}/restore`, {}
      ),
    // K-V1: the browse-index preference. Listing never gates hireability
    // (hire runs through a stall) and unlisting never 404s a direct
    // resolve — this flips ONLY presence in the public directory.
    setAgentListing: (agentId: string, isListed: boolean) =>
      call<{ agent_id: string; is_listed: boolean }>(
        'PUT', `/api/v1/agents/${agentId}/listing`, { is_listed: isListed }
      ),

    // ---- Tenant onboarding (PR #115 spec) --------------------------------
    // The two verbs the console could not do: bring an agent and a stall
    // into existence. Everything downstream (certify / go-live / fund)
    // already exists above.

    // Path A: the server mints the keypair and returns the private key in
    // this response and NOWHERE else. See RegisterAgentPathAResponse.
    registerAgentGenerate: (input: {
      display_name: string;
      agent_type: AgentType;
      description?: string;
      operator_id: string;
      domains?: string[];
    }) =>
      call<RegisterAgentPathAResponse>('POST', '/api/v1/agents/register', {
        action: 'generate_identity',
        display_name: input.display_name,
        agent_type: input.agent_type,
        description: input.description || undefined,
        platform_origin: 'aexgora-stoa-console',
        operator_id: input.operator_id,
        domains: input.domains ?? [],
      }),
    // Path B: the tenant proves ownership of an existing AEX identity by
    // signing a server-issued challenge ON THEIR OWN MACHINE and pasting
    // the signature. The console never asks for, sees, or accepts a
    // private key on this path (PR #115 spec §3.5).
    registerAgentExisting: (input: {
      aex_id: string;
      display_name: string;
      agent_type: AgentType;
      description?: string;
      operator_id: string;
      domains?: string[];
      proof_of_ownership: { challenge: string; signature: string };
    }) =>
      call<RegisterAgentPathBResponse>('POST', '/api/v1/agents/register', {
        action: 'register_existing',
        aex_id: input.aex_id,
        display_name: input.display_name,
        agent_type: input.agent_type,
        description: input.description || undefined,
        platform_origin: 'aexgora-stoa-console',
        operator_id: input.operator_id,
        domains: input.domains ?? [],
        proof_of_ownership: input.proof_of_ownership,
      }),
    // Operator Stripe Connect (routes/operators.ts). Creates the operator's
    // Express account on first call, and returns a fresh onboarding link on
    // every call; 503 PAYMENT_NOT_CONFIGURED when the board has no Stripe.
    connectStripe: (returnUrl: string) =>
      call<{ account_id: string; onboarding_url: string }>(
        'POST', '/api/v1/operators/stripe/connect', { return_url: returnUrl }
      ),

    // ---- Offerings -------------------------------------------------------
    // A priced `action` content item is an offering: the manifest and the
    // board index list it with a hire_url (stall-content.ts
    // buildStallProjection). The write takes operator Bearer for the
    // stall's owner, so no agent key is needed here.
    publishOffering: (stallId: string, input: {
      slug: string;
      title: string;
      summary?: string;
      price_aex: number;
    }) =>
      call<StallContentItem>('POST', `/api/v1/stoa/stalls/${stallId}/content`, {
        type: 'action',
        slug: input.slug,
        title: input.title,
        summary: input.summary || undefined,
        price_aex: input.price_aex,
        visibility: 'public',
        published: true,
      }),
    // What a buyer reads: 404 until the stall is live (and, if private,
    // outside its colony).
    getStallManifest: (stallId: string) =>
      call<{ offerings?: StallOffering[] }>(
        'GET', `/api/v1/stoa/stalls/${stallId}/manifest`, undefined
      ),

    getAuthChallenge: () =>
      call<AuthChallenge>('GET', '/api/v1/auth/challenge', undefined),
    // is_private is required here even though the server would default it:
    // visibility must be an explicit choice at every layer of this flow
    // (founder ruling 2026-08-13 — no silent default, ever).
    createStall: (input: {
      name: string;
      description?: string;
      agent_id: string;
      is_private: boolean;
    }) =>
      call<CreateStallResponse>('POST', '/api/v1/stoa/stalls', {
        name: input.name,
        description: input.description || undefined,
        agent_id: input.agent_id,
        is_private: input.is_private,
      }),
    // Agent-perspective inbox: jobs dispatched TO this agent (assigned, or
    // posted to a stall this agent owns). Ponder 2026-05-20.
    getAgentInbox: (agentId: string, params?: { stoa_status?: string; limit?: number; offset?: number }) => {
      const qs = new URLSearchParams();
      if (params?.stoa_status) qs.set('stoa_status', params.stoa_status);
      if (params?.limit) qs.set('limit', String(params.limit));
      if (params?.offset) qs.set('offset', String(params.offset));
      const q = qs.toString();
      return call<{ agent_id: string; jobs: StoaJob[]; total: number; limit: number; offset: number }>(
        'GET', `/api/v1/stoa/console/agents/${agentId}/inbox${q ? `?${q}` : ''}`, undefined
      );
    },
    // Agent-perspective outbox: jobs this agent posted via /stoa/jobs/by-agent.
    getAgentOutbox: (agentId: string, params?: { stoa_status?: string; limit?: number; offset?: number }) => {
      const qs = new URLSearchParams();
      if (params?.stoa_status) qs.set('stoa_status', params.stoa_status);
      if (params?.limit) qs.set('limit', String(params.limit));
      if (params?.offset) qs.set('offset', String(params.offset));
      const q = qs.toString();
      return call<{ agent_id: string; jobs: StoaJob[]; total: number; limit: number; offset: number }>(
        'GET', `/api/v1/stoa/console/agents/${agentId}/outbox${q ? `?${q}` : ''}`, undefined
      );
    },
    // Agent-posted job — agent-key auth variant (persona-runtime).
    postJobByAgent: (input: {
      stall_id: string;
      title: string;
      description: string;
      offered_rate: number;
      parent_job_id: string;
      is_private?: boolean;
      assigned_agent_id?: string;
      rate_currency?: string;
      rate_unit?: string;
      expires_at?: string | null;
    }) =>
      call<{
        id: string;
        escrow_payment_id: string;
        stoa_status: 'posted';
        parent_job_id: string;
        posted_by_agent_id: string;
        posted_by_stall_id: string;
        expires_at: string | null;
      }>('POST', '/api/v1/stoa/jobs/by-agent', input),
    // Chat-protocol layer (Founder 2026-05-20). Fast-path comments.
    getJobComments: (jobId: string, params?: { limit?: number; offset?: number }) => {
      const qs = new URLSearchParams();
      if (params?.limit) qs.set('limit', String(params.limit));
      if (params?.offset) qs.set('offset', String(params.offset));
      const q = qs.toString();
      return call<{ job_id: string; comments: StoaJobComment[]; total: number; limit: number; offset: number }>(
        'GET', `/api/v1/stoa/jobs/${jobId}/comments${q ? `?${q}` : ''}`, undefined
      );
    },
    postJobCommentAsOperator: (jobId: string, input: { body: string; author_agent_id: string }) =>
      call<StoaJobComment>('POST', `/api/v1/stoa/jobs/${jobId}/comments-as-operator`, input),
    // Operator-side proxy: lets the Console post as an owned agent using
    // the operator Bearer token. Mirrors /deliver-as-agent pattern.
    postJobByAgentAsOperator: (input: {
      stall_id: string;
      title: string;
      description: string;
      offered_rate: number;
      parent_job_id: string;
      posted_by_agent_id: string;
      is_private?: boolean;
      assigned_agent_id?: string;
      rate_currency?: string;
      rate_unit?: string;
      expires_at?: string | null;
    }) =>
      call<{
        id: string;
        escrow_payment_id: string;
        stoa_status: 'posted';
        parent_job_id: string;
        posted_by_agent_id: string;
        posted_by_stall_id: string;
        expires_at: string | null;
      }>('POST', '/api/v1/stoa/jobs/by-agent-as-operator', input),

    // ---- Ops tab ----------------------------------------------------------
    // Operator/admin controls that previously existed only as API routes with
    // no surface: the money kill-switch, the stall lifecycle, and per-surface
    // subkeys (migration 085). See the Ops tab in ConsolePlaceholder.

    // /health is unauthenticated and NOT under the /api/v1 prefix.
    getHealth: () =>
      call<PlatformHealth>('GET', '/health', undefined),

    // AEXTech-admin only (adminEmails allowlist) — a non-admin operator gets
    // 403 here even though every other call on this client works for them.
    setSpendHalt: (input: { halted: boolean; reason?: string }) =>
      call<SpendHaltState>('POST', '/api/v1/admin/spend-halt', input),

    getHungJobs: () =>
      call<{ ttl_minutes: number; blocked: HungJob[]; dead: HungJob[] }>(
        'GET', '/api/v1/stoa/console/hung-jobs', undefined
      ),

    // Stall lifecycle. certify -> go-live is a deliberate two-step ceremony;
    // archive is a soft delete that `restore` can undo, which is why the Ops
    // tab shows them as a pair.
    // The route requires a `summary` object (stallCertifySchema) and stores
    // it as certification_summary; an empty body answers 400. Certification
    // here is the owner's own attestation, so the summary says exactly that.
    certifyStall: (stallId: string) =>
      call<{ stall_id: string; is_certified: boolean }>('POST', `/api/v1/stoa/stalls/${stallId}/certify`, {
        summary: { source: 'stoa-console', attested_by: 'stall_owner' },
      }),
    goLiveStall: (stallId: string) =>
      call<{ stall_id: string; is_live: boolean }>('POST', `/api/v1/stoa/stalls/${stallId}/go-live`, {}),
    // archiveStall / restoreStall / fundStall already exist above — the Stalls
    // tab uses them.

    // Per-surface subkeys. The private key comes back EXACTLY ONCE, on mint —
    // the list route never returns secret material.
    listSubkeys: (agentId: string) =>
      call<{ subkeys: AgentSubkey[] }>('GET', `/api/v1/agents/${agentId}/subkeys`, undefined),
    mintSubkey: (agentId: string, input: { label: string }) =>
      call<{ subkey: AgentSubkey; private_key: string }>('POST', `/api/v1/agents/${agentId}/subkeys`, input),
    revokeSubkey: (agentId: string, subkeyId: string, reason?: string) =>
      call<{ subkey: AgentSubkey }>('DELETE', `/api/v1/agents/${agentId}/subkeys/${subkeyId}`, { reason }),

    // Duty switches (migration 087). Reading is operator-auth; SETTING is
    // AEXTech-admin, same bar as the spend halt — this is a remote enable
    // switch for autonomous agents that spend money.
    getDutySwitches: () =>
      call<{ switches: DutySwitch[] }>('GET', '/api/v1/duty/switches', undefined),
    setDutySwitch: (key: string, input: { switched_on: boolean; kind?: 'seat' | 'global' | 'product'; reason?: string }) =>
      call<{ switch: DutySwitch }>('PUT', `/api/v1/duty/switches/${encodeURIComponent(key)}`, input),
    getDutySwitchLog: (key?: string) =>
      call<{ entries: DutySwitchLogEntry[] }>(
        'GET', `/api/v1/duty/switches/log${key ? `?switch_key=${encodeURIComponent(key)}` : ''}`, undefined
      ),
  };
}

export type OperatorApi = ReturnType<typeof createOperatorApi>;
